import type { RenderQuality } from '../../rendering/renderQuality';
import {
  BEAT_PULSE_STRENGTH,
  COMBO_BLOOM_MAX_ALPHA,
  COMBO_BLOOM_MIN_ENERGY,
  COMBO_BLOOM_RADIUS_FRAC,
  VIGNETTE_BASE_ALPHA,
  VIGNETTE_COMBO_ALPHA_BONUS,
} from './jumpRenderConstants';
import { hexToRgba } from './jumpRenderColour';

/**
 * Ambient atmosphere layers that frame the Jump playfield without touching the
 * gameplay draws:
 *
 *   - **Combo bloom** — a soft radial wash anchored on the cube column that
 *     brightens with the "in the zone" combo energy, so a long streak literally
 *     lights up the stage. Drawn as a background pass (behind obstacles).
 *   - **Vignette** — a cached corner darkening that funnels the eye toward the
 *     playfield and tightens slightly as combo energy climbs. Drawn over the
 *     gameplay but beneath the HUD/overlays so text stays crisp.
 *
 * Both gradients are cached behind a dimension+colour key (no per-frame
 * allocation), respect the render-quality flags (`backgroundBloom` /
 * `decorations`), and carry no motion of their own beyond a brightness term —
 * safe under reduced motion.
 */
export class JumpSceneAtmosphere {
  private bloomGradient: CanvasGradient | null = null;
  private bloomKey = '';
  private vignetteGradient: CanvasGradient | null = null;
  private vignetteKey = '';

  /** Drop cached gradients so the next draw rebuilds them at the new size. */
  invalidate(): void {
    this.bloomGradient = null;
    this.bloomKey = '';
    this.vignetteGradient = null;
    this.vignetteKey = '';
  }

  /**
   * Radial combo bloom centred on `(centreX, centreY)` — the cube's column.
   * Brightness scales with `comboEnergy`; a small beat-synced term adds a pulse
   * unless reduced motion is active. Skipped entirely on the low tier
   * (`backgroundBloom` off) and below {@link COMBO_BLOOM_MIN_ENERGY}.
   */
  drawComboBloom(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    centreX: number,
    centreY: number,
    comboEnergy: number,
    accentColour: string,
    beatPulse: number,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    if (!quality.backgroundBloom || comboEnergy < COMBO_BLOOM_MIN_ENERGY) {
      return;
    }
    const radius = Math.max(120, Math.max(width, height) * COMBO_BLOOM_RADIUS_FRAC);
    const key = `${Math.round(centreX)}|${Math.round(centreY)}|${Math.round(radius)}|${accentColour}`;
    if (!this.bloomGradient || this.bloomKey !== key) {
      const grad = ctx.createRadialGradient(centreX, centreY, 0, centreX, centreY, radius);
      grad.addColorStop(0, hexToRgba(accentColour, 1));
      grad.addColorStop(0.45, hexToRgba(accentColour, 0.4));
      grad.addColorStop(1, hexToRgba(accentColour, 0));
      this.bloomGradient = grad;
      this.bloomKey = key;
    }
    const pulse = reducedMotion ? 0 : beatPulse * BEAT_PULSE_STRENGTH * 0.5;
    const alpha = comboEnergy * COMBO_BLOOM_MAX_ALPHA * (1 + pulse);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = alpha;
    ctx.fillStyle = this.bloomGradient;
    ctx.fillRect(centreX - radius, centreY - radius, radius * 2, radius * 2);
    ctx.restore();
  }

  /**
   * Soft darkening at the canvas corners. Cached per-size; the alpha lifts a
   * touch with combo energy so the playfield "tunnels" in when the player is on
   * a streak. Skipped on the low tier (`decorations` off).
   */
  drawVignette(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    comboEnergy: number,
    quality: RenderQuality,
  ): void {
    if (!quality.decorations) {
      return;
    }
    const key = `${width}|${height}`;
    if (!this.vignetteGradient || this.vignetteKey !== key) {
      const maxDim = Math.max(width, height);
      const grad = ctx.createRadialGradient(
        width / 2,
        height / 2,
        maxDim * 0.28,
        width / 2,
        height / 2,
        maxDim * 0.72,
      );
      grad.addColorStop(0, 'rgba(0, 0, 0, 0)');
      grad.addColorStop(1, 'rgba(0, 0, 0, 1)');
      this.vignetteGradient = grad;
      this.vignetteKey = key;
    }
    const alpha = VIGNETTE_BASE_ALPHA + comboEnergy * VIGNETTE_COMBO_ALPHA_BONUS;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = this.vignetteGradient;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }
}
